import {
  useCallback,
  FunctionComponent,
  SyntheticEvent,
  useEffect,
  ChangeEvent,
  useMemo,
} from 'react';
import _ from 'lodash';
import {
  OpenCaseInput as OpenCaseInputTypes,
  OpenCase as OpenCaseTypes,
} from '~types';
import { Button, ButtonProps } from '~ui/Button';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useMutation, gql } from '@apollo/client';
import { setTimeout } from 'isomorphic-timers-promises';
import { Price, convertNumber } from '~ui/Price';
import { useConfig, useProfile } from '~components/hooks';

import { useGetCase } from '../../home/hooks';
import { useOpenModal } from '../../modals/hooks';
import {
  useGameCount,
  useStatusGame,
  useGames,
  useSliderStyle,
  useSliderOpenStyle,
  useFastMode,
  useCalceOfPossibleFreeOpenPrice,
} from '../hooks';
import Currency from '../../Currency';
import playAudio from '~frontend/utils/playAudio';
import FormatDate from '../../home/cases/FormatDate';
import { useKeyPressEvent } from 'react-use';

interface CSSStyleDeclarationMoz extends CSSStyleDeclaration {
  mozTransform: string;
  webkitTransform: string;
}

interface ButtonsCaseMutationData {
  openCase: OpenCaseTypes[];
}

interface ButtonsCaseMutationVars {
  input: OpenCaseInputTypes;
}

let audioInterval = null;
let abortController = new AbortController();

const getTransformOffset = (element: Element) => {
  const style = window.getComputedStyle(element) as CSSStyleDeclarationMoz;
  const matrix = style.transform || style.webkitTransform || style.mozTransform;

  const t = matrix.split(',');

  return 6 === t.length
    ? parseInt(t[4])
    : 16 === t.length
    ? parseInt(t[12])
    : 0;
};

export const OpenCase: FunctionComponent<ButtonProps> = props => {
  const [isFastGame] = useFastMode();
  const [countOpen, setGameCount] = useGameCount();
  const getConfig = useConfig();
  const { onShow } = useOpenModal();
  const [, setGames] = useGames();
  const [gameStatus, setStatus] = useStatusGame();

  const [, setSliderStyle] = useSliderStyle();
  const [, setSliderOpenStyle] = useSliderOpenStyle();
  const { t } = useTranslation();

  const {
    data: { getCase },
    refetch,
  } = useGetCase({});

  const [openCase, { loading }] = useMutation<
    ButtonsCaseMutationData,
    ButtonsCaseMutationVars
  >(
    gql`
      mutation OpenCase($input: OpenCaseInput!) {
        openCase(input: $input) {
          getCategory {
            id
            getName
          }
          getGame {
            id
            status
            price
            priceSell
            chance
            getCase {
              id
              price
            }
            getItem {
              id
              getName
              getImage
              color
              quality
            }
          }
          getItems {
            id
            getName
            getImage
            color
            quality
          }
        }
      }
    `,
  );

  const onOpenCase = useCallback(
    async (event: SyntheticEvent<HTMLElement>) => {
      try {
        setStatus(1);
        setGames([]);

        playAudio(event, '12.mp3', 0.2);

        const isDemo = event.currentTarget.getAttribute('data-type') === 'demo';

        if (isDemo) {
          setGameCount(1);
        }

        const {
          data: { openCase: getGames },
        } = await openCase({
          variables: {
            input: {
              caseId: getCase?.id,
              countOpen,
              isFastGame,
              isDemo,
            },
          },
        });

        if (!isDemo) {
          // window?.fbq('track', 'AddToCart', {
          //   content_name: getCase?.getName,
          //   content_type: 'product',
          //   value: convertNumber(getCase?.price),
          //   currency: getConfig?.getCurrency?.char,
          //   content_ids: [getCase?.id],
          //   contents: [{ id: getCase?.id, quantity: countOpen }],
          // });

          window?.dataLayer?.push({ ecommerce: null });
          window?.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: getConfig?.getCurrency?.char,
              value: convertNumber(getCase?.price),
              items: [
                {
                  item_name: getCase?.getName,
                  item_id: String(getCase?.id),
                  price: convertNumber(getCase?.price),
                  item_category: String(getCase?.category),
                  quantity: countOpen,
                  currency: getConfig?.getCurrency?.char,
                },
              ],
            },
          });

          for (const { getGame, getCategory } of getGames) {
            window?.dataLayer?.push({ ecommerce: null }); // Clear the previous ecommerce object.
            window?.dataLayer?.push({
              event: 'playCase',
              ecommerce: {
                transaction_id: String(getGame.id),
                value: convertNumber(getCase?.price),
                currency: window['currency'],
                affiliation: 'onOpenCase',
                items: [
                  {
                    item_name: getCase?.getName,
                    item_id: String(getCase?.id),
                    item_category: getCategory?.getName,
                    price: convertNumber(getCase?.price),
                    quantity: 1,
                  },
                ],
              },
            });
          }
        }

        setGames(getGames);

        if (isFastGame) {
          playAudio(event, 'cs_stinger.wav', 0.2);
        } else {
          setStatus(2);

          const timeout = 13000;

          await setTimeout(500, '', { signal: abortController.signal });

          const obj = document.querySelector(
            `.case-slider-item:nth-child(${countOpen > 1 ? 70 : 68})`,
          ) as HTMLElement;

          setSliderStyle({
            timeout,
          });

          setSliderOpenStyle({
            opacity: 1,
          });

          let m = 10,
            position = 0;

          audioInterval = setInterval(() => {
            (position = getTransformOffset(
              document
                .querySelectorAll('.case-play-slider')[0]
                .querySelectorAll(`.case-play-slider-wrap`)[0],
            )),
              m - position >= 180 &&
                ((m = position), playAudio(event, 'roulette-start-2.mp3', 0.1));
          }, 80);

          await setTimeout(timeout, '', { signal: abortController.signal });

          clearInterval(audioInterval);

          playAudio(event, 'cs_stinger.wav', 0.2);

          setStatus(3);

          await setTimeout(3500, '', { signal: abortController.signal });

          setSliderOpenStyle({
            opacity: 0,
          });

          await setTimeout(500, '', { signal: abortController.signal });

          setSliderStyle(null);
        }

        setStatus(4);

        if (isDemo) {
          onShow('WinDemoModal', { getGame: getGames[0] });

          setStatus(0);
        } else {
          if (getCase?.type === 3) {
            await refetch();
          }
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          setStatus(0);
          toast.error(
            <>
              <div className="notify-title">{t('Error')}!</div>
              <div
                className="notify-text"
                dangerouslySetInnerHTML={{ __html: t(error.message) }}
              />
            </>,
          );
        }
      }
    },
    [
      setStatus,
      setGames,
      openCase,
      getCase?.id,
      getCase?.price,
      getCase?.getName,
      getCase?.category,
      getCase?.type,
      countOpen,
      isFastGame,
      setGameCount,
      getConfig?.getCurrency?.char,
      setSliderStyle,
      setSliderOpenStyle,
      onShow,
      refetch,
      t,
    ],
  );

  const disabled = useMemo(() => {
    if (gameStatus > 0) {
      return true;
    }

    return props?.disabled;
  }, [gameStatus, props?.disabled]);

  return (
    <Button
      onClick={onOpenCase}
      {...props}
      loading={loading}
      disabled={disabled}
    />
  );
};

const ButtonsCase: FunctionComponent = () => {
  const getProfile = useProfile();
  const [, setSliderStyle] = useSliderStyle();
  const [, setSliderOpenStyle] = useSliderOpenStyle();
  const [gameStatus, setStatus] = useStatusGame();
  const { onShow } = useOpenModal();
  const [isFastGame, setFastGame] = useFastMode();
  const [countOpen, setGameCount] = useGameCount();
  const [isGame] = useStatusGame();
  const { t } = useTranslation();
  const getConfig = useConfig();

  const {
    data: { getCase },
  } = useGetCase({});

  useKeyPressEvent(
    ({ code }) => code === 'Digit1',
    () => {
      const button = document.querySelector(
        '.case-select-count-item[data-quant="1"]',
      );

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  useKeyPressEvent(
    ({ code }) => code === 'Digit2',
    () => {
      const button = document.querySelector(
        '.case-select-count-item[data-quant="2"]',
      );

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  useKeyPressEvent(
    ({ code }) => code === 'Digit3',
    () => {
      const button = document.querySelector(
        '.case-select-count-item[data-quant="3"]',
      );

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  useKeyPressEvent(
    ({ code }) => code === 'Digit4',
    () => {
      const button = document.querySelector(
        '.case-select-count-item[data-quant="5"]',
      );

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  // useKeyPressEvent(
  //   ({ code }) => code === 'Digit5',
  //   () => {
  //     const button = document.querySelector(
  //       '.case-select-count-item[data-quant="10"]',
  //     );

  //     if (button instanceof HTMLElement) {
  //       button.click();
  //     }
  //   },
  // );

  useKeyPressEvent(
    ({ code }) => code === 'KeyO',
    () => {
      const button = document.querySelector('.button[data-type="openCase"]');

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );
  useKeyPressEvent(
    ({ code }) => code === 'KeyD',
    () => {
      const button = document.querySelector('.button[data-type="demo"]');

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );
  useKeyPressEvent(
    ({ code }) => code === 'KeyQ',
    () => {
      const button = document.querySelector(
        '.btn-fast-open input[type="checkbox"]',
      );

      if (button instanceof HTMLElement) {
        button.click();
      }
    },
  );

  useEffect(() => {
    abortController = new AbortController();

    return () => {
      clearInterval(audioInterval);
      abortController.abort();
      setGameCount(1);
      setStatus(0);
      setSliderOpenStyle({
        opacity: 0,
      });
      setSliderStyle(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toogleFastMode = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFastGame(event.currentTarget.checked);
    },
    [setFastGame],
  );

  const quant = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();

      if (isGame > 0) {
        return false;
      }

      setGameCount(_.toInteger(event.currentTarget.getAttribute('data-quant')));

      return true;
    },
    [isGame, setGameCount],
  );

  const viewModal = useCallback(
    (
      event: SyntheticEvent<HTMLElement>,
      { type, reachgoal } = event.currentTarget.dataset,
    ) => {
      if (event) {
        event.preventDefault();
      }

      if (!type) {
        throw Error('Не указан тип');
      }

      onShow(type, { reachgoal });
    },
    [onShow],
  );

  const [price, priceOld, freeCountOpen, battlePassCoins] =
    useCalceOfPossibleFreeOpenPrice(
      getCase,
      getCase?.userOpenForFree,
      countOpen,
    );

  const disabled = useMemo(() => {
    if (getCase?.type === 7 && freeCountOpen < 1) {
      return true;
    }

    return false;
  }, [getCase?.type, freeCountOpen]);

  if (isGame) {
    return null;
  }

  return (
    <div className={`case-btns-wrap ${price <= 0 && 'btns-wrap-free'}`}>
      {getCase?.getDateEnd && (
        <FormatDate
          getCase={getCase}
          className="distribution-item-controls-timer"
        />
      )}

      {getCase?.needToOpenForFree > 0 && (
        <div className="case-item-nums">
          <div className="case-item-nums-wrap">
            <span className="icon icon-case-name"></span>
            <span className="red">{getCase?.userOpenForFree}</span>
            <span>&nbsp;/&nbsp;</span>
            <span>{getCase?.needToOpenForFree}</span>
          </div>
        </div>
      )}

      {getCase?.type === 1 && getCase?.getLeft > 0 && (
        <div className="case-item-nums">
          <div className="case-item-nums-wrap">
            <span className="fa fa-bolt icon icon-case-name"></span>
            <span className="red">{getCase?.getLeft}</span>
            <span> / </span>
            <span>{getCase?.getLimit}</span>
          </div>
        </div>
      )}

      {![3, 7].includes(getCase?.type) && (
        <>
          {getProfile.money > 0 && (
            <div
            className="case-select-count"
            data-rar={price <= 0 ? '06c50c' : getCase?.color}
            >
              {[1, 2, 3, 5].map(item => (
                <button
                  key={item}
                  className={`case-select-count-item ${
                    countOpen === item && 'active'
                  }`}
                  onClick={quant}
                  data-quant={item}
                >
                  <div className="btn-hoykey">
                    {item === 5 ? 4 : item === 10 ? 5 : item}
                  </div>
                  {item}x
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {!gameStatus && getProfile.money < price ? (
        <>
          <div className="case-select-count"></div>
          <div className="case-warning case-warning-dont-money">
            <span className="icon material-icons">campaign</span>
            <div className="case-warning-title">
              {t('Just a little to open the case')}!
            </div>
            {
              <div className="case-warning-text">
                {t('By replenishing the account at least')}{' '}
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={price - getProfile?.money} />
                  </>
                ) : (
                  <>
                    <Price sum={price - getProfile?.money} />
                    <Currency />
                  </>
                )}{' '}
                {t(
                  'or by selling items for this amount, you can immediately open it',
                )}
                !
              </div>
            }
          </div>
          <a
            className="btn-pay btn-case-free popolnit_case"
            href="#"
            data-type="FundsModal"
            data-reachgoal="add_funds_case"
            onClick={viewModal}
            onMouseEnter={playAudio}
            data-audio="2.mp3"
            data-volume="0.20"
          >
            <div className="btn-hoykey">p</div>
            <div className="top"></div>
            <div className="top-2"></div>
            <div className="bot"></div>
            <div className="bot-2"></div>
            <div className="shadow"></div>
            <div className="shadow-2"></div>
            <div className="text">{t('Refill')}</div>
          </a>
        </>
      ) : (
        <OpenCase
          disabled={disabled}
          className={`btn-buy ${
            getCase?.type === 3 && 'open_free_case_profil'
          }`}
          data-rar={getCase?.color}
          data-type="openCase"
        >
          <div className="btn-hoykey">o</div>

          <div className="btn-sale-cost-wrap">
            {freeCountOpen > 0 && (
              <div className="btn-free-case-count">{freeCountOpen}</div>
            )}
            {getCase?.priceOld > 0 && (
              <div className="btn-sale-cost">
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={priceOld} />
                  </>
                ) : (
                  <>
                    <Price sum={priceOld} />
                    <Currency />
                  </>
                )}
              </div>
            )}
            {battlePassCoins > 0 && (
              <div className="btn-coin-cost">
                <span>
                  +<Price sum={battlePassCoins} />
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g opacity="0.5">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM9 13.917C11.8377 13.441 14 10.973 14 8C14 5.027 11.8377 2.55904 9 2.08296V5H11L12 6H7L6 7V9L7 10H12L11 11H9V13.917ZM7 13.917V11H6L4 9V7L6 5H7V2.08296C4.16229 2.55904 2 5.027 2 8C2 10.973 4.16229 13.441 7 13.917Z"
                      fill="white"
                    ></path>
                  </g>
                </svg>
              </div>
            )}
          </div>

          <span className="btn-buy-text">{t('Open')}</span>
          <span className="btn-buy-cost">
            {!getCase?.price || price <= 0 ? (
              <>
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    Free | 0
                  </>
                ) : (
                  <>
                    Free | 0
                    <Currency />
                  </>
                )}
              </>
            ) : (
              <>
                {getConfig.isEU ? (
                  <>
                    <Currency />
                    <Price sum={price} />
                  </>
                ) : (
                  <>
                    <Price sum={price} />
                    <Currency />
                  </>
                )}
              </>
            )}
          </span>
        </OpenCase>
      )}

      {getCase?.type !== 6 && (
        <OpenCase
          data-rar={price <= 0 ? '06c50c' : getCase?.color}
          data-type="demo"
          className={`btn-demo b-violet b-violet-blue ${
            getProfile?.money < getCase?.price ? 'demover-0' : 'demover-1'
          }`}
        >
          <div className="btn-hoykey">d</div>
          {t('btn-demo b-violet b-violet-blue')}
        </OpenCase>
      )}

      {getCase?.type === 3 && !getProfile?.lastFreeGameDate ? null : (
        <div className="btn-fast-open">
          <span>{t('Open quick')}</span>
          <label className="switch" htmlFor="checkbox">
            <div className="btn-hoykey">q</div>
            <input
              type="checkbox"
              id="checkbox"
              onChange={toogleFastMode}
              defaultChecked={isFastGame}
            />
            <div className="switch-round"></div>
          </label>
        </div>
      )}
    </div>
  );
};

export default ButtonsCase;
